/**
 * Shared utilities for feature flags
 */

import { useCallback } from 'react';
import { FeatureFlagAttributes } from '@/types/admin/featureFlags';
import { useAdminColors } from '@/hooks/admin/useAdminColors';

/**
 * Filter feature flags by search term
 */
export const filterFeatureFlags = (
  flags: FeatureFlagAttributes[],
  searchTerm: string
): FeatureFlagAttributes[] => {
  if (!searchTerm.trim()) return flags;

  const searchLower = searchTerm.toLowerCase();
  return flags.filter((flag: FeatureFlagAttributes) =>
    (flag.propertyName?.toLowerCase() || '').includes(searchLower) ||
    (flag.organizationName?.toLowerCase() || '').includes(searchLower) ||
    Object.entries(flag.flags).some(
      ([key, value]) =>
        key.toLowerCase().includes(searchLower) ||
        value.toString().toLowerCase().includes(searchLower)
    )
  );
};

/**
 * Group feature flags by organization name
 */
export const groupFlagsByOrganization = (
  flags: FeatureFlagAttributes[]
): Record<string, FeatureFlagAttributes[]> => {
  const grouped: Record<string, FeatureFlagAttributes[]> = {};

  flags.forEach((flag: FeatureFlagAttributes) => {
    const orgName = flag.organizationName || 'Unknown';
    if (!grouped[orgName]) {
      grouped[orgName] = [];
    }
    grouped[orgName].push(flag);
  });

  return grouped;
};

/**
 * Theme-aware org-dot color picker. Returns a stable function that maps an
 * org name to the right brand/status accent for the current theme.
 */
export function useOrgDotColor(): (orgName: string) => string {
  const { redesign, semantic } = useAdminColors();
  return useCallback(
    (orgName: string) => {
      if (orgName === 'Core Spaces') return redesign.accent.blueBg;
      if (orgName === 'GMH University Housing') return semantic.status.success;
      return redesign.text.subtle;
    },
    [redesign.accent.blueBg, semantic.status.success, redesign.text.subtle],
  );
}

/**
 * Extract organization ID from feature flag (handles both camelCase and snake_case)
 */
export const getOrganizationId = (flag: FeatureFlagAttributes): number | undefined => {
  return (flag as any)?.organizationId || flag?.organization_id;
};

/**
 * Get property ID from feature flag
 */
export const getPropertyId = (flag: FeatureFlagAttributes): number | undefined => {
  return flag.property_id || flag.id;
};

/**
 * Calculate override count for a feature flag
 */
export const getOverrideCount = (flag: FeatureFlagAttributes): number => {
  return Object.keys(flag.flags).length;
};

/**
 * Format override count text
 */
export const formatOverrideText = (count: number): string => {
  return `${count} override${count !== 1 ? 's' : ''}`;
};
